import { type ReactNode, useState } from 'react';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster, toast } from 'sonner';
import { TooltipProvider } from '@/shared/components/ui/tooltip';
import { CommandPaletteContextProvider } from '@/shared/components/CommandPaletteContext';
import { AuthProvider, TenantProvider, ThemeProvider, ShellProvider } from '@/providers';
import { getEnv, isDevelopment } from '@/config/env';
import { ApiError } from '@/api/errors';

function handleQueryError(error: unknown) {
  if (error instanceof ApiError && error.status === 401) {
    return;
  }
  const message = error instanceof Error ? error.message : 'Request failed';
  toast.error(message);
}

type AppProvidersProps = {
  children: ReactNode;
};

/** Root provider tree. */
export function AppProviders({ children }: AppProvidersProps) {
  getEnv();

  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({ onError: handleQueryError }),
        mutationCache: new MutationCache({
          onError: (error) => handleQueryError(error),
        }),
        defaultOptions: {
          queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: true },
        },
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <AuthProvider>
            <TenantProvider>
              <ShellProvider>
                <CommandPaletteContextProvider>
                  {children}
                  <Toaster position="bottom-right" richColors closeButton />
                </CommandPaletteContextProvider>
              </ShellProvider>
            </TenantProvider>
          </AuthProvider>
        </TooltipProvider>
      </ThemeProvider>
      {isDevelopment() && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
